import type { Entity } from '@backstage/catalog-model';

/**
 * Helper function to normalize entity references for consistent comparison
 * 
 * @param ref - Entity reference string (e.g., 'system-name', 'namespace/system-name', 'kind:namespace/name')
 * @param kind - Entity kind (default: 'system')
 * @returns Normalized reference in 'kind:namespace/name' format
 */
export const normalizeEntityRef = (ref: string, kind = 'system'): string => {
  // If already in 'kind:namespace/name' format, return as is
  if (ref.includes(':')) return ref.toLowerCase();
  
  // Otherwise, assume 'namespace/name' or just 'name'
  const parts = ref.split('/');
  
  if (parts.length === 2) {
    // Format: 'namespace/name'
    return `${kind}:${parts[0].toLowerCase()}/${parts[1].toLowerCase()}`;
  } else if (parts.length === 1) {
    // Format: 'name' - assume default namespace
    return `${kind}:default/${parts[0].toLowerCase()}`;
  } else if (parts.length > 2) {
    // Multi-level namespace (e.g., 'team/project/name')
    const name = parts.pop()!; // Safe to use non-null assertion since length > 2
    const namespace = parts.join('/').toLowerCase();
    return `${kind}:${namespace}/${name.toLowerCase()}`;
  }
  
  return ref.toLowerCase();
};

/**
 * Helper function to get a normalized entity reference from an Entity object
 * 
 * @param entity - The Entity object
 * @returns Normalized reference in 'kind:namespace/name' format
 */
export const getEntityRef = (entity: Entity): string => {
  return `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
};

/**
 * Helper function to check if an entity belongs to a specific system
 * Handles various namespace formats and prevents false positives
 * 
 * @param entitySystemRef - The system reference from entity.spec.system
 * @param systemName - The system name to match against
 * @returns true if entity belongs to the system
 */
export const belongsToSystem = (entitySystemRef: string, systemName: string): boolean => {
  // Normalize the system reference for comparison
  const normalizedRef = entitySystemRef.toLowerCase();
  const normalizedName = systemName.toLowerCase();
  
  // Direct match
  if (normalizedRef === normalizedName) return true;
  
  // Match with default namespace
  if (normalizedRef === `default/${normalizedName}`) return true;
  
  // Match with any namespace - ensure it's the full name after the last slash
  // This prevents false positives like "payment-system" matching "system"
  const lastSlashIndex = normalizedRef.lastIndexOf('/');
  if (lastSlashIndex !== -1) {
    const nameAfterSlash = normalizedRef.substring(lastSlashIndex + 1);
    return nameAfterSlash === normalizedName;
  }
  
  return false;
};
